/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes, { string } from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Grid, Link, Pagination, Typography, withTheme } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { CMLTextSnippet } from 'bundles/cml';
import { ForumThreadCard } from 'bundles/discussions/components/personalization-ForumThreadCard';
import PostsForYouTabDataProvider from 'bundles/discussions/components/personalization/__components/__providers/PostsForYouTabDataProvider';
import { defaultPageSize } from 'bundles/discussions/constants';
import {
  buildUrl,
  getForumLink,
  getGroupForumLink,
  getWeekDiscussionsUrl,
} from 'bundles/discussions/utils/discussionsUrl';
import { handlePaginationAriaLabel } from 'bundles/discussions/utils/handlePaginationAriaLabel';
import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/discussions';

import type { ForumPostSearchResult } from '../__providers/__types__';

const getThreadUrl = (post: ForumPostSearchResult) => {
  const { forumPost, forumInfo } = post;
  const forumType = forumInfo?.forumType;
  const forumId = forumInfo?.forumId;
  const forumGroupId = forumInfo?.forumScope?.courseGroupForumScope?.groupId;
  const threadId = forumPost.topLevelPostId;

  if (!forumType || !forumId) return undefined;
  if ('courseBranchWeekForumType' in forumType) {
    return buildUrl(getWeekDiscussionsUrl(forumType.courseBranchWeekForumType.weekNumber), threadId);
  } else if ('courseGroupRootForumType' in forumType) {
    return buildUrl(getGroupForumLink(forumGroupId), threadId);
  } else {
    return buildUrl(getForumLink(forumId), threadId);
  }
};

const styles = (theme: Theme) =>
  css({
    listStyle: 'none',
    padding: `var(--cds-spacing-300) 0`,
    position: 'relative',
    borderBottom: `1px solid var(--cds-color-grey-50)`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&.focused, &:hover, &.focused a': {
      textDecoration: 'none',
    },
    '.forReader': {
      height: 0,
      width: 0,
    },
  });

const renderPost = (post: ForumPostSearchResult, theme: Theme) => {
  const { forumPost, creatorProfile, lastRepliedUserProfile } = post;
  const threadUrl = getThreadUrl(post);
  const TopLevelThreadCreator = {
    ...creatorProfile,
    userId: Number(creatorProfile?.userId),
    id: creatorProfile?.userId,
    learnerId: Number(creatorProfile?.userId),
  };
  const lastReplier = lastRepliedUserProfile
    ? {
        ...lastRepliedUserProfile,
        userId: Number(lastRepliedUserProfile.userId),
        id: lastRepliedUserProfile?.userId,
        learnerId: Number(lastRepliedUserProfile?.userId),
      }
    : undefined;

  return (
    <li css={styles(theme)} aria-label={_t('Post: #{post}', { post: forumPost.title })}>
      <ForumThreadCard
        answerCount={parseInt(forumPost.topLevelReplyCount, 10)}
        badge={forumPost.badges?.[0]}
        createdAt={forumPost.createdAtTime}
        creator={TopLevelThreadCreator}
        description={<CMLTextSnippet cml={{ typeName: 'cml', definition: forumPost.content.cmlContent }} />}
        forumName={post?.forumInfo?.forumTitle}
        forumQuestionId={forumPost.topLevelPostId}
        isUpvoted={forumPost.isUpvoted}
        lastContributor={lastReplier || TopLevelThreadCreator}
        lastReplyDate={forumPost.lastRepliedAtTime}
        pinned={forumPost.isPinned}
        replyCount={parseInt(forumPost.topLevelReplyCount, 10)}
        threadUrl={threadUrl}
        title={forumPost.title}
        titleLink={
          (threadUrl && (
            <Typography variant="h2semibold" className="rc-personalization_ForumThreadCard__title">
              <Link
                component={TrackedReactLink}
                href={threadUrl}
                trackingName="PostsForYouTab_PostLink"
                aria-label={_t('Post: #{post}', { post: forumPost.title })}
                data={{
                  question_id: forumPost.topLevelPostId,
                  question_user_id: forumPost.creatorUserId,
                }}
              >
                {forumPost.title}
              </Link>
            </Typography>
          )) ||
          undefined
        }
        upvoteCount={parseInt(forumPost.upvoteCount, 10)}
      />
    </li>
  );
};

class PostsForYouTabBody extends React.Component<{ theme: Theme; hasBeenVisited?: boolean }> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    courseId: string.isRequired,
  };

  declare context: LegacyContextType<typeof PostsForYouTabBody.contextTypes>;

  onPageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    const router = this.context.router;
    router.push({
      pathname: router.location.pathname,
      params: {},
      query: Object.assign(router.location.query, { page }),
    });
  };

  render() {
    const { router, courseId } = this.context;
    const { theme, hasBeenVisited } = this.props;
    const currentPage = router.location.query.page ? parseInt(router.location.query.page, 10) : 1;
    return (
      <div className="rc-personalization_PostsForYouTabBody">
        <PostsForYouTabDataProvider courseId={courseId} currentPage={currentPage} hasBeenVisited={hasBeenVisited}>
          {({ loading, error, data }) => {
            if (error) return null;
            if (loading) {
              return (
                <Typography
                  variant="h2semibold"
                  css={css`
                    margin-top: var(--cds-spacing-300);
                  `}
                >
                  {_t('Loading...')}
                </Typography>
              );
            }
            if (data) {
              if (data.postResults.length === 0) {
                return (
                  <Typography
                    variant="h2semibold"
                    css={css`
                      margin-top: var(--cds-spacing-300);
                    `}
                  >
                    {_t('No posts yet')}
                  </Typography>
                );
              }

              const pageCount = Math.ceil(parseInt(data.totalHits, 10) / 15);
              const showPaginationControls = pageCount > 1;
              const total = parseInt(data.totalHits, 10);

              return (
                <React.Fragment>
                  <ul>
                    {data.postResults.map((post) => (
                      <React.Fragment key={post.forumPost.postId}>{renderPost(post, theme)}</React.Fragment>
                    ))}
                  </ul>
                  {showPaginationControls && (
                    <Grid container item xs={12} css={{ justifyContent: 'center' }}>
                      <Pagination
                        onPageChange={this.onPageChange}
                        total={total}
                        page={currentPage}
                        defaultPageSize={defaultPageSize}
                        getItemAriaLabel={handlePaginationAriaLabel}
                        hidePageSizeInput
                      />
                    </Grid>
                  )}
                </React.Fragment>
              );
            }
            return null;
          }}
        </PostsForYouTabDataProvider>
      </div>
    );
  }
}

export default withTheme(PostsForYouTabBody);
